import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  getOrderDepthsAndLatestTrades,
  getTickers,
  getListTradeBaseOnPeriod,
  getAnnualClosePrices,
  getHistoricalShares,
  getHistoricalPrices,
  getCurrenciesName,
} from "../services";
import { companySettingsSelector, instrumentsSettingsSelector,  } from "../store/slice/settings/selectors";
import { getAnnualClosePricesSelector, instrumentsIndexerSelector } from "../store/slice/trade";


/**  
 * @typedef {import("../..").IGetAnnualClosePricesQuery} IGetAnnualClosePricesQuery
 * @typedef { import("../../").IInstrument[] } IInstrumentSource 
 * @typedef { import("../../").IHistoricalPricesSearch } IHistoricalPricesSearch
 * @typedef { import("../../").IHistoricalPrices } IHistoricalPrices
 * @typedef {{
 *  toCurrency: null | string;
 *  fromDate: string;
 *  toDate: string;
 *  instrumentId: number[]
 * }} getHistoricalPricesActionParams
 */


export const getTickersAction = createAsyncThunk("trade/tickers", async (ids = [], { getState, dispatch }) => {
  const settings = companySettingsSelector(getState());
  const tickers = await getTickers(ids);
  const indexer = (acc, item ) => {
    acc[item.id] = item;
    return acc
  }
  const instrumentsIndex = settings.instruments.reduce(indexer, {})
  const peersIndex = settings.peers.reduce(indexer, {})
  
  tickers.data.instruments.map((item) => {
    const instrumentSetting = instrumentsIndex[item.instrumentId];
    const peersSetting = peersIndex[item.instrumentId];
    let itemSetting = instrumentSetting;
    if(peersSetting) {
      itemSetting = peersSetting
    }
    
    // replace custom field from settings
    item.ticker = itemSetting.tickerName ?? item.ticker;
    item.currencyCode = itemSetting.currencyCode ?? item.currencyCode;
    item.marketName = itemSetting.marketName ?? item.marketName;
    item.shareName = itemSetting.shareName ?? item.shareName;
    item.marketAbbreviation = itemSetting.marketAbbreviation ?? item.marketAbbreviation;
    item.order = itemSetting.order === null ? 0 : itemSetting.order

    if(peersSetting) {
      item.order = item.order + 10000 
    }

    return item;
  });

  tickers.data.instruments = tickers.data.instruments.sort((a, b) => {
    return a.order - b.order
  });



  const annual = getAnnualClosePricesSelector(getState());

  // we choice getTickersAction is place for first load AnnualClosePrices
  // because load AnnualClosePrices in useEffect component is bad practice
  // useEffect run twice in first init and AnnualClosePrices depend Tickers.
  // because of that, we can't load AnnualClosePrices when component first mount 
  if(!annual.loaded) {
    const instrumentsSettings = instrumentsSettingsSelector(getState())
    const instrumentDataIndexer = tickers.data.instruments.reduce((acc, item) => {
      acc[item.instrumentId] = item
      return acc;
    }, {})
    const shares = [];
  
    for (let item of instrumentsSettings) {
      shares.push({
        id: item.id,
        isin: instrumentDataIndexer[item.id].iSIN,
        marketId: instrumentDataIndexer[item.id].marketID,
        currency: instrumentDataIndexer[item.id].currencyCode,
      });
    }
    dispatch(getAnnualClosePricesAction({ shares, currency: null }))
  }
  return tickers;
});

export const getOrderDepthsAndLatestTradesAction = createAsyncThunk(
  "trade/OrderDepthsAndLatestTrades",
  async id => {
    return await getOrderDepthsAndLatestTrades(id);
  }
);

export const getListTradeBaseOnPeriodAction = createAsyncThunk(
  "trade/GetListTradeBaseOnPeriod",
  async (queryData, {getState}) => {
    const response = await getListTradeBaseOnPeriod(queryData);
    const data = response.data.sharePrices || []
    const instruments = getState().trade.instruments || {}

    // Check when each item have all null value, display '-'
    const checkData = data.map(item => {
      const newItem = {...item};
      delete newItem.instrumentId;

      return  ({
        ...item,
        isNullAllData: Object.keys(newItem).every(x => !newItem[x]),
        shareName: instruments[item.instrumentId].shareName || "N/A",
        marketAbbreviation: instruments[item.instrumentId].marketAbbreviation  || "N/A",
        ticker: instruments[item.instrumentId].ticker  || "N/A",
        currency: item.currency || instruments[item.instrumentId].currencyCode,
      })
    })
    return checkData
  }
);

export const getHistoricalSharesAction = createAsyncThunk(
  "trade/GetHistoricalShares",
  async (queryData, {getState}) => {
    const data =  await getHistoricalShares(queryData);
    const instruments = getState().trade.instruments || {}
    const convertData = data.data.historicalShares.map(x =>({
      ...x,  
      shareName: instruments[x.instrumentId].shareName || x.shareName || "N/A",
      currency: x.currency || instruments[x.instrumentId].currencyCode ||  "N/A",
      marketName: instruments[x.instrumentId].marketName  || "N/A",
      marketAbbreviation: instruments[x.instrumentId].marketAbbreviation  || "N/A",
      ticker: instruments[x.instrumentId].ticker  || "N/A",
      order: instruments[x.instrumentId].order
    })
  ); 
    return convertData
  }
);

export const getAnnualClosePricesAction = createAsyncThunk(
  "trade/GetAnnualClosePrices",
  async (/** @type { {shares: IHistoricalPricesSearch['shares'], currency: string | null } } */ {shares, currency}, { getState }) => {
    const numberOfYearOnChartDefault = shares.length > 1 ? 5 : 10; // default 5 years for singleTicker and 10 years for more than multiple ticker
    const setttings = companySettingsSelector(getState())
    const search = {
      companyCode: /** @type { string } */ window.appSettings.companyCode,
      yearDuration: setttings.numberOfYearOnChart || numberOfYearOnChartDefault,
      useMarchFiscalYearEnd: setttings.useMarchFiscalYearEnd,
      currency,
      shares
    }
    const result = await getAnnualClosePrices(search);

    // variable determine whether annual-close-price display (true: hidden - false: visible)
    // false: 
    // having only 1 ticker and that ticker has more than 1 year
    // having more than 1 ticker and each ticker has at least one year
    let isAllLessTwoYearAnnualClosePrices = true
   
    if(result.data.annualClosePrices.length === 1 && result.data.annualClosePrices[0].datas.length > 1) {
      isAllLessTwoYearAnnualClosePrices = false
    }
    result.data.annualClosePrices = result.data.annualClosePrices.map(item => {
      if(isAllLessTwoYearAnnualClosePrices && result.data.annualClosePrices.length > 1 && item.datas.length >= 1) {
        isAllLessTwoYearAnnualClosePrices = false
      }
      return ({
        currency, // we add more field currency for everyone can access to it
        ...item,
      })
    })

    return {
      ...result,
      isAllLessTwoYearAnnualClosePrices
    };
  }
);

export const getHistoricalPricesAction = createAsyncThunk(
  "trade/getHistoricalPrices",
  async (/** @type { getHistoricalPricesActionParams } */search, { getState }) => {
    const tickerIndexer = instrumentsIndexerSelector(getState())
    const listTicker = (await Promise.all(search.instrumentId.map(id => {
      return getHistoricalPrices({...search, instrumentId: id});
    })))
    
    let historicalPrices = listTicker.map((item, index) => {
      const instrumentId = search.instrumentId[index]
      const ticker = tickerIndexer[instrumentId]
      return {
        datas: item.data.instrumentHistorical,
        currency: search.currency || ticker.currencyCode,
        shareName: ticker.shareName,
        ticker: ticker.ticker,
        marketAbbreviation: ticker.marketAbbreviation,
        marketId: ticker.marketID,
        isin: ticker.iSIN,
        instrumentId: instrumentId,
      }
    })
    return {
      data: {
        historicalPrices: historicalPrices.filter(item => item.datas.length > 0)
      }   
    }
  }
);

export const getCurrenciesNameAction = createAsyncThunk('currency/getCurrenciesName', async (currencies) => {
  return await getCurrenciesName(currencies)
})
